import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useBrand } from "../theme/ThemeProvider";
import { BrandLogo } from "../theme/BrandLogo";
import { IntervalToggle } from "../components/IntervalToggle";
import { api, type BillingState, type PlanCatalogEntry } from "../lib/api";
import {
  groupPlansByInterval,
  intervalSavingsLabel,
  isLocked,
  planSummary,
  price,
  rowForInterval,
} from "../lib/plans";
import { useBillingPoll } from "../lib/useBillingPoll";
import { captureEvent } from "../lib/observability";

type Interval = "month" | "year";

// Post-login landing for accounts with no active plan yet
// (read_model === 'locked'): the checkout step of the trial funnel (A6).
// Reuses the SignIn/Pending `.signin` shell (no sidebar — this isn't a
// dashboard page) and is routed OUTSIDE the authenticated Shell in
// App.tsx. Entitled accounts are redirected straight to /dashboard so a
// bookmark or back-nav to /welcome never traps them here.
export function Welcome() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const checkoutReturn = searchParams.get("checkout") === "return";

  const [state, setState] = useState<BillingState | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<Interval>("month");
  const viewedRef = useRef(false);

  // Only actually polls when checkoutReturn is true; otherwise idle.
  const poll = useBillingPoll(checkoutReturn);

  useEffect(() => {
    // caller role, either way — drives the member-vs-owner copy below.
    api
      .me()
      .then((m) => setRole(m.role))
      .catch(() => {
        /* best-effort — role gating just falls back to showing the picker */
      });
  }, []);

  // Plain landing (not a checkout return): fetch once, bounce entitled
  // accounts to the dashboard, and fire the one welcome_viewed event.
  useEffect(() => {
    if (checkoutReturn) return;
    api
      .billing()
      .then((s) => {
        setState(s);
        const locked = isLocked(s.entitlement);
        if (!viewedRef.current) {
          viewedRef.current = true;
          captureEvent("welcome_viewed", { locked });
        }
        if (!locked) navigate("/dashboard", { replace: true });
      })
      .catch((e) => setErr(String((e as Error).message)));
  }, [checkoutReturn, navigate]);

  // Checkout-return leg: the account was locked when it left for LS
  // checkout, so welcome_viewed(locked:true) + checkout_returned both
  // fire on this mount; useBillingPoll then drives "Finishing setup…".
  useEffect(() => {
    if (!checkoutReturn) return;
    captureEvent("checkout_returned");
    if (!viewedRef.current) {
      viewedRef.current = true;
      captureEvent("welcome_viewed", { locked: true });
    }
  }, [checkoutReturn]);

  useEffect(() => {
    if (checkoutReturn && poll.status === "success") {
      captureEvent("trial_activated");
      navigate("/dashboard", { replace: true });
    }
  }, [checkoutReturn, poll.status, navigate]);

  async function checkout(p: PlanCatalogEntry) {
    setErr(null);
    setBusy(p.provider_variant_id);
    try {
      captureEvent("checkout_started", {
        plan: p.plan,
        interval: p.billing_interval,
      });
      const { url } = await api.createCheckout(p.provider_variant_id, "welcome");
      window.location.href = url;
    } catch (ex) {
      setErr(String((ex as Error).message));
      setBusy(null);
    }
  }

  const groups = state ? groupPlansByInterval(state.plans, true) : [];

  return (
    <div className="center signin">
      <div className="signin-card" style={{ maxWidth: 640 }}>
        <div className="signin-mark">
          <BrandLogo alt="" />
        </div>

        {checkoutReturn ? (
          <>
            <span className="eyebrow">Setting up</span>
            {poll.status !== "timeout" && (
              <>
                <h1 className="signin-title">Finishing setup…</h1>
                <p className="signin-tagline">
                  We're confirming your subscription with Lemon Squeezy —
                  this usually takes a few seconds.
                </p>
              </>
            )}
            {poll.status === "timeout" && (
              <>
                <h1 className="signin-title">Almost there</h1>
                <p className="signin-tagline">
                  Your payment may still be processing. Check{" "}
                  <a href="/billing">Billing</a> in a moment, or contact{" "}
                  <a href={`mailto:${brand.supportEmail}`}>
                    {brand.supportEmail}
                  </a>{" "}
                  if it doesn't update.
                </p>
              </>
            )}
          </>
        ) : (
          <>
            <span className="eyebrow">Start your trial</span>
            <h1 className="signin-title">Welcome to {brand.productName}</h1>
            <p className="signin-tagline">
              Pick a plan to start your 14-day free trial. Search and company
              profiles stay open either way, and you can cancel any time.
            </p>

            {err && <p className="error">{err}</p>}
            {!state && !err && <p className="muted">Loading…</p>}

            {state && !state.checkout_configured && (
              <p className="muted">
                Self-serve checkout isn’t enabled yet — contact{" "}
                <a href={`mailto:${brand.supportEmail}`}>
                  {brand.supportEmail}
                </a>{" "}
                to get started.
              </p>
            )}

            {state && state.checkout_configured && role === "member" && (
              <p className="muted">
                Ask your account owner to start the subscription.
              </p>
            )}

            {state && state.checkout_configured && role !== "member" && (
              <>
                <div
                  className="seg"
                  role="tablist"
                  aria-label="Billing interval"
                  style={{
                    justifyContent: "center",
                    marginBottom: "var(--space-4)",
                  }}
                >
                  <IntervalToggle
                    value={billingInterval}
                    onChange={setBillingInterval}
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--space-3)",
                    textAlign: "left",
                  }}
                >
                  {groups.map((g) => {
                    const row = rowForInterval(g, billingInterval);
                    if (!row) return null;
                    const savings =
                      billingInterval === "year"
                        ? intervalSavingsLabel(g.month, g.year)
                        : null;
                    return (
                      <div
                        key={g.plan}
                        className="card"
                        style={{ padding: "var(--space-3)" }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: "var(--space-3)",
                          }}
                        >
                          <strong>{g.displayName}</strong>
                          <span className="muted">{price(row)}</span>
                        </div>
                        <p className="muted" style={{ margin: "var(--space-2) 0" }}>
                          {planSummary(row)}
                          {savings && ` · ${savings}`}
                        </p>
                        <button
                          className="btn primary"
                          style={{ marginTop: "var(--space-2)" }}
                          disabled={busy !== null}
                          onClick={() => checkout(row)}
                        >
                          {busy === row.provider_variant_id
                            ? "Opening…"
                            : "Start free trial"}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <div className="signin-actions" style={{ marginTop: "var(--space-5)" }}>
              <button
                type="button"
                className="btn secondary signin-cta"
                onClick={() => navigate("/dashboard")}
              >
                Skip for now
              </button>
            </div>

            <p className="signin-fineprint">
              <a href="https://plaingraph.com/#showcase">
                Browse sample companies
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
